import React, { useState } from 'react';
import Icon from '../AppIcon';

const ProgressContextPanel = ({
  challengeData = null,
  mode = 'panel',
  isOpen = false,
  onClose,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDesktopAchievements, setShowDesktopAchievements] = useState(false);

  const defaultData = {
    challengeName: '30일 운동 챌린지',
    currentProgress: 15,
    totalSteps: 30,
    completedProofs: 15,
    starRating: 4,
    nextUnlock: '20일 달성 시 특별 배지 획득',
    recentAchievements: [
      { id: 1, title: '첫 주 완료', date: '2026-01-01', icon: 'Award' },
      { id: 2, title: '10일 연속 달성', date: '2025-12-28', icon: 'Flame' },
    ],
  };

  const data = challengeData || defaultData;

  const progressPercentage = (data?.currentProgress / data?.totalSteps) * 100;

  const PanelContent = (
    <div className="bg-card rounded-lg shadow-gaming-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-foreground">
          진행 상황
        </h3>
        <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/20 border border-primary/30">
          {[...Array(5)]?.map((_, index) => (
            <Icon
              key={index}
              name="Star"
              size={16}
              color="currentColor"
              className={index < data?.starRating ? 'fill-current text-primary' : 'text-muted-foreground'}
            />
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground caption">
            {data?.challengeName}
          </span>
          <span className="text-sm font-medium text-foreground data-text">
            {data?.currentProgress}/{data?.totalSteps}
          </span>
        </div>
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-gaming rounded-full"
            style={{
              width: `${progressPercentage}%`,
            }}
          />
        </div>
      </div>

      <div className="flex items-center space-x-3 p-4 bg-success/10 rounded-md border border-success/20">
        <div className="flex-shrink-0 w-10 h-10 bg-success/20 rounded-md flex items-center justify-center">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium text-foreground">
            {data?.completedProofs}개 증명 완료
          </div>
          <div className="text-xs text-muted-foreground caption">
            계속 진행하세요!
          </div>
        </div>
      </div>

      {data?.nextUnlock && (
        <div className="flex items-start space-x-3 p-4 bg-accent/10 rounded-md border border-accent/20">
          <div className="flex-shrink-0 w-10 h-10 bg-accent/20 rounded-md flex items-center justify-center">
            <Icon name="Lock" size={20} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-foreground mb-1">
              다음 잠금 해제
            </div>
            <div className="text-xs text-muted-foreground caption">
              {data?.nextUnlock}
            </div>
          </div>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowDesktopAchievements((prev) => !prev)}
          className="w-full flex items-center justify-between text-left transition-gaming"
        >
          <h4 className="text-sm font-heading font-semibold text-foreground">
            최근 업적
          </h4>
          <Icon
            name={showDesktopAchievements ? 'ChevronUp' : 'ChevronDown'}
            size={18}
            className="text-muted-foreground"
          />
        </button>
        {showDesktopAchievements && (
          <div className="space-y-2 mt-3">
            {data?.recentAchievements?.map((achievement) => (
              <div
                key={achievement?.id}
                className="flex items-center space-x-3 p-3 bg-muted/50 rounded-md transition-gaming hover:bg-muted"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-md flex items-center justify-center">
                  <Icon
                    name={achievement?.icon}
                    size={16}
                    color="var(--color-primary)"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {achievement?.title}
                  </div>
                  <div className="text-xs text-muted-foreground caption">
                    {achievement?.date}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (mode === 'modal') {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-dialog">
        <div
          className="absolute inset-0 bg-background/60"
          onClick={() => onClose?.()}
          aria-hidden="true"
        />
        <div
          className="absolute top-20 right-4 md:right-8 w-[min(24rem,calc(100vw-2rem))]"
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="bg-card rounded-lg shadow-gaming-lg border border-border overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="text-sm font-heading font-semibold text-foreground">
                진행 상황
              </div>
              <button
                type="button"
                onClick={() => onClose?.()}
                className="p-2 rounded-md hover:bg-muted transition-gaming"
                aria-label="닫기"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4">
              {PanelContent}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="hidden lg:block fixed top-20 right-8 w-80 z-contextual">
        {PanelContent}
      </div>
      <div className="lg:hidden fixed top-20 left-0 right-0 z-contextual">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full bg-card border-b border-border px-4 py-3 flex items-center justify-between transition-gaming"
        >
          <div className="flex items-center space-x-3">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">
              진행 상황: {data?.currentProgress}/{data?.totalSteps}
            </span>
          </div>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            className="text-muted-foreground"
          />
        </button>

        {isExpanded && (
          <div
            className="bg-card border-b border-border px-4 py-4 space-y-4 shadow-gaming-md"
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground caption">
                  {data?.challengeName}
                </span>
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/20 border border-primary/30">
                  {[...Array(5)]?.map((_, index) => (
                    <Icon
                      key={index}
                      name="Star"
                      size={14}
                      color="currentColor"
                      className={index < data?.starRating ? 'fill-current text-primary' : 'text-muted-foreground'}
                    />
                  ))}
                </div>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-gaming rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-success/10 rounded-md border border-success/20">
              <Icon name="CheckCircle" size={18} color="var(--color-success)" />
              <span className="text-sm text-foreground">
                {data?.completedProofs}개 증명 완료
              </span>
            </div>

            {data?.nextUnlock && (
              <div className="flex items-start space-x-3 p-3 bg-accent/10 rounded-md border border-accent/20">
                <Icon name="Lock" size={18} color="var(--color-accent)" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground mb-1">
                    다음 잠금 해제
                  </div>
                  <div className="text-xs text-muted-foreground caption">
                    {data?.nextUnlock}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProgressContextPanel;